import { sameGraphStructure } from './GraphRenderState';
import { QueryClient } from "@tanstack/react-query";

import { ExpressionMetadataModel, FlowMetadataModel, ParameterType, VariableTypeMetadata } from "../../../api/api";
import { FlowQueries } from "../Hooks/useFlowQueries";
import {
	ExpressionModel,
} from "../Models/ExpressionModel";
import { ObjectPropertyType } from "../Models/ObjectPropertyType";
import { VarExpressionModel } from "../Models/VarExpressionModel";
import { ConstExpressionModel } from "../Models/ConstExpressionModel";
import { ExpressionTypes } from "../Models/ExpressionTypes";
import { ActivityNode, FlowEdge, FlowNode } from "../Models/GraphModel";
import { SchemaValueModel } from '../Models/SchemaValueModel';
import { IVariableScope } from "../Models/VariableScopes/IVariableScope";
import { VariableScope } from "../Models/VariableScopes/VariableScope";
import { VariableScopeCalculatorServiceBase } from "./VariableScopeCalculatorServiceBase";
import { narrowVariableScope } from './SchemaScopeNarrowing';
import { isInvocationScopeEdge } from './ScopeGraphSemantics';
import { mergeScopeBranches, scopeFingerprint } from './ScopeMerge';
import { scopeGraphComponents } from './ScopeGraphComponents';
import { isAgentToolTransition } from './AgentToolGraph';
import { ActivityCategories } from '../Models/ActivityConstants';
import { AGENT_TOOL_INPUT_SCHEMA_CATEGORY } from '../Models/FlowModel';

type ActivityVariableScope = {
	enter: VariableScope;
	leave: VariableScope;
};

export default class ActivityVariableScopeCalculatorService extends VariableScopeCalculatorServiceBase {
	private rootVariableScope: VariableScope;
	private nodes: FlowNode[];
	private nodeById = new Map<string, FlowNode>();
	private incomingById = new Map<string, FlowEdge[]>();
	private dependents = new Map<string, Set<string>>();
	private signatures = new Map<string, string>();
	private schemaSignatures = new Map<string, string>();
	private activityByName: Map<string, FlowMetadataModel['activities'][number]>;
	private expressionByName: Map<string, ExpressionMetadataModel>;
	private queue: Promise<unknown> = Promise.resolve();
	private sliceDeadline = 0;
	private edges: FlowEdge[];
	private variableScopeByActivity: Map<string, ActivityVariableScope>;
	private parentContentScopeByActivity: Map<string, VariableScope>;
	private solving = false;
	private flowMetadata: FlowMetadataModel;

	constructor(
		flowMetadata: FlowMetadataModel,
		rootVariableScope: VariableScope,
		nodes: FlowNode[],
		edges: FlowEdge[],
		flowQueries: FlowQueries,
		queryClient: QueryClient,
		schemaValues: SchemaValueModel[] = [],
	) {
		super(flowQueries, queryClient, schemaValues);
		this.flowMetadata = flowMetadata;
		for (const schema of schemaValues) this.schemaSignatures.set(schema.Id, JSON.stringify(schema));
		this.activityByName = new Map(flowMetadata.activities.map((item) => [item.name, item]));
		this.expressionByName = new Map(flowMetadata.expressions.map((item) => [item.name, item]));
		this.rootVariableScope = rootVariableScope;
		this.nodes = nodes;
		this.edges = edges;
		this.variableScopeByActivity = new Map();
		this.parentContentScopeByActivity = new Map();
		for (const node of nodes) {
			this.nodeById.set(node.id, node);
		}
		const depend = (source: string, target: string) => {
			if (!this.dependents.has(source)) { this.dependents.set(source, new Set()); }
			this.dependents.get(source)!.add(target);
		};
		// Retain disabled edges as declared conditional branches for authoring scope.
		const toolRoots = new Set(edges.filter(isAgentToolTransition).map((edge) => edge.target));
		const ordinaryEntries = new Set(edges.filter((edge) => !isAgentToolTransition(edge)).map((edge) => edge.target));
		for (const edge of edges) {
			const source = this.nodeById.get(edge.source);
			const target = this.nodeById.get(edge.target);
			const sourceMetadata = source ? this.activityByName.get(source.data.Type) : undefined;
			const targetMetadata = target ? this.activityByName.get(target.data.Type) : undefined;
			if (toolRoots.has(edge.source) && sourceMetadata?.categories?.includes(ActivityCategories.AGENT)
				&& !isInvocationScopeEdge(edge, targetMetadata)
				&& !ordinaryEntries.has(edge.source)) continue;
			if (!this.incomingById.has(edge.target)) { this.incomingById.set(edge.target, []); }
			this.incomingById.get(edge.target)!.push(edge);
			depend(edge.source, edge.target);
		}
		for (const node of nodes) {
			if (node.parentId) { depend(node.parentId, node.id); }
			const { Metadata: _Metadata, Transitions: _Transitions, ...definition } = node.data as Record<string, unknown>;
			this.signatures.set(node.id, JSON.stringify([
				node.parentId, definition,
				(this.incomingById.get(node.id) ?? []).map((edge) => [edge.source, edge.data?.Type, edge.data?.Condition]),
			]));
		}
	}

	protected override getExpressionMetadata(expressionType: string): ExpressionMetadataModel | undefined {
		return this.expressionByName.get(expressionType);
	}

	/** Serialize public requests so overlapping selections are not mistaken for graph cycles. */
	getVariableScopeAtActivity(id: string): Promise<ActivityVariableScope> {
		const request = this.queue.then(async () => {
			this.sliceDeadline = performance.now() + 8;
			if (!this.variableScopeByActivity.has(id)) await this.solveScopes(id);
			return this.calculateCachedScope(id);
		});
		this.queue = request.catch(() => undefined);
		return request;
	}

	withSchemaValues(schemaValues: SchemaValueModel[]): ActivityVariableScopeCalculatorService {
		if (schemaValues === this.schemaValues) return this;
		const revisions = new Map(schemaValues.map((schema) => [schema.Id, JSON.stringify(schema)]));
		const changedSchemas = new Set([...Array.from(this.schemaSignatures.keys()), ...Array.from(revisions.keys())].filter(
			(id) => this.schemaSignatures.get(id) !== revisions.get(id)));
		if (!changedSchemas.size) return this;
		const next = new ActivityVariableScopeCalculatorService(this.flowMetadata, this.rootVariableScope,
			this.nodes, this.edges, this.flowQueries, this.queryClient, schemaValues);
		const usesChangedSchema = (value: unknown): boolean => {
			if (!value || typeof value !== 'object') return false;
			const record = value as Record<string, unknown>;
			return (typeof record.SchemaValueId === 'string' && changedSchemas.has(record.SchemaValueId))
				|| Object.values(record).some(usesChangedSchema);
		};
		const invalid = new Set(this.nodes.filter((node) => usesChangedSchema(node.data)).map((node) => node.id));
		const pending = Array.from(invalid);
		for (let index = 0; index < pending.length; index++) {
			for (const dependent of Array.from(this.dependents.get(pending[index]) ?? [])) {
				if (!invalid.has(dependent)) { invalid.add(dependent); pending.push(dependent); }
			}
		}
		this.variableScopeByActivity.forEach((scope, id) => {
			if (!invalid.has(id)) next.variableScopeByActivity.set(id, scope);
		});
		this.parentContentScopeByActivity.forEach((scope, id) => {
			if (!invalid.has(id)) next.parentContentScopeByActivity.set(id, scope);
		});
		return next;
	}

	/** An isolated graph revision retains completed results only outside the changed dependency closure. */
	withGraph(nodes: FlowNode[], edges: FlowEdge[]): ActivityVariableScopeCalculatorService {
		if (nodes === this.nodes && edges === this.edges) { return this; }
		if (sameGraphStructure(this.nodes, nodes) && edges.length === this.edges.length && edges.every((edge, index) => {
			const previous = this.edges[index];
			return edge === previous || (edge.source === previous.source && edge.target === previous.target && edge.data === previous.data);
		})) {
			this.nodes = nodes;
			this.edges = edges;
			return this;
		}
		const next = new ActivityVariableScopeCalculatorService(this.flowMetadata, this.rootVariableScope, nodes, edges, this.flowQueries, this.queryClient, this.schemaValues);
		next.schemaEntityCache = this.schemaEntityCache;
		const changed = new Set<string>();
		this.signatures.forEach((signature, id) => {
			if (next.signatures.get(id) !== signature) { changed.add(id); }
		});
		next.signatures.forEach((signature, id) => {
			if (this.signatures.get(id) !== signature) { changed.add(id); }
		});
		if (!changed.size) {
			this.nodes = nodes;
			this.edges = edges;
			return this;
		}
		const pending = Array.from(changed);
		for (let i = 0; i < pending.length; i++) {
			for (const graph of [this, next]) {
				graph.dependents.get(pending[i])?.forEach((id) => {
					if (!changed.has(id)) { changed.add(id); pending.push(id); }
				});
			}
		}
		this.variableScopeByActivity.forEach((scope, id) => {
			if (!changed.has(id)) { next.variableScopeByActivity.set(id, scope); }
		});
		this.parentContentScopeByActivity.forEach((scope, id) => {
			if (!changed.has(id)) { next.parentContentScopeByActivity.set(id, scope); }
		});
		return next;
	}

	private async calculateCachedScope(id: string): Promise<ActivityVariableScope> {
		const cached = this.variableScopeByActivity.get(id);
		if (cached) return cached;
		if (!this.nodeById.has(id)) throw new Error(`Activity with id ${id} not found`);
		if (this.solving) throw new Error(`Scope dependency '${id}' was not initialized`);
		await this.solveScopes(id);
		return this.variableScopeByActivity.get(id)!;
	}

	/** Solve the uncached ancestor closure; loop results no longer depend on selection order. */
	private async solveScopes(id: string): Promise<void> {
		const pending = [id];
		const affected = new Set<string>();
		while (pending.length) {
			const current = pending.pop()!;
			if (affected.has(current) || this.variableScopeByActivity.has(current)) continue;
			const node = this.nodeById.get(current);
			if (!node) throw new Error(`Activity with id ${current} not found`);
			affected.add(current);
			if (node.parentId) pending.push(node.parentId);
			for (const edge of this.incomingById.get(current) ?? []) pending.push(edge.source);
		}
		const ids = Array.from(affected).sort();
		for (const current of ids) {
			const enter = new VariableScope(); enter.addPrevious(this.rootVariableScope);
			const leave = new VariableScope(); leave.addPrevious(enter);
			this.variableScopeByActivity.set(current, { enter, leave });
		}
		this.solving = true;
		try {
			const dependencies = new Map(ids.map((current): [string, string[]] => [current, Array.from(new Set([
				...(this.incomingById.get(current) ?? []).map((edge) => edge.source),
				...(this.nodeById.get(current)?.parentId ? [this.nodeById.get(current)!.parentId!] : []),
			])).filter((dependency) => affected.has(dependency))]));
			for (const component of scopeGraphComponents(dependencies)) {
				const cyclic = component.length > 1 || dependencies.get(component[0])?.includes(component[0]);
				let converged = !cyclic;
				for (let iteration = 0; iteration <= (cyclic ? component.length * 2 + 1 : 0); iteration++) {
					let changed = false;
					this.parentContentScopeByActivity.clear();
					for (const current of component) {
						const previous = this.variableScopeByActivity.get(current)!;
						const next = await this.calculateVariableScope(this.nodeById.get(current) as ActivityNode);
						if (cyclic) changed ||= scopeFingerprint(previous.enter) !== scopeFingerprint(next.enter)
							|| scopeFingerprint(previous.leave) !== scopeFingerprint(next.leave);
						this.variableScopeByActivity.set(current, next);
					}
					if (!changed) { converged = true; break; }
				}
				if (!converged) {
					for (const current of component) {
						const scope = this.variableScopeByActivity.get(current)!;
						scope.enter.addDiagnostic('Loop scope did not stabilize. Check recursively growing variable assignments.');
						scope.leave.addDiagnostic('Loop scope did not stabilize. Check recursively growing variable assignments.');
					}
				}
			}
		} catch (error) {
			for (const current of ids) this.variableScopeByActivity.delete(current);
			throw error;
		} finally {
			this.solving = false;
			this.parentContentScopeByActivity.clear();
		}
	}

	private async yieldIfNeeded(): Promise<void> {
		if (performance.now() >= this.sliceDeadline) {
			await new Promise<void>((resolve) => setTimeout(resolve, 0));
			this.sliceDeadline = performance.now() + 8;
		}
	}

	private async calculateVariableScope(activityNode: ActivityNode): Promise<ActivityVariableScope> {
		await this.yieldIfNeeded();
		const activityMetadata = this.activityByName.get(activityNode.data.Type);
		if (!activityMetadata) {
			throw new Error(`Activity with type ${activityNode.data.Type} not found`);
		}

		let variableScopeEnter = new VariableScope();
		const variableScopeLeave = new VariableScope();

		// Traverse incoming edges to find parent scopes
		const incomingEdges = (this.incomingById.get(activityNode.id) ?? []);
		if (incomingEdges.length === 0) {
			if (activityNode.parentId) {
				// Nested activity inside a block: inherit from parent block's content scope
				const parentContentScope = await this.getParentContentScope(activityNode.parentId);
				variableScopeEnter.addPrevious(parentContentScope);
			} else {
				// If no incoming edges, inherit from root scope
				variableScopeEnter.addPrevious(this.rootVariableScope);
			}
		} else {
			const incomingScopes: VariableScope[] = [];
			for (const edge of incomingEdges) {
				const sourceNode = this.nodeById.get(edge.source);
				if (sourceNode) {
					const sourceVariableScope = await this.calculateCachedScope(sourceNode.id);
					const sourceScope = isInvocationScopeEdge(edge, activityMetadata) ? sourceVariableScope.enter : sourceVariableScope.leave;
					incomingScopes.push(narrowVariableScope(sourceScope, edge.data?.Condition as ExpressionModel | undefined));
				}
			}
			variableScopeEnter = mergeScopeBranches(incomingScopes);
		}

		const inputDeclarations = activityMetadata.variableSets?.filter((variable) => activityMetadata.parameters.some(
			(parameter) => parameter.name === variable.typeRef && parameter.categories?.includes(AGENT_TOOL_INPUT_SCHEMA_CATEGORY)));
		await this.processVariableSets(inputDeclarations, activityMetadata.parameters, activityNode.data,
			variableScopeEnter, variableScopeEnter, `activity '${activityNode.data.Type}' (${activityNode.id}) input`);

		await this.yieldIfNeeded();

		// Leaving scope inherits from the entering scope
		variableScopeLeave.addPrevious(variableScopeEnter);

		// Add activity-specific variables and process expression side effects
		await this.processVariableSets(
			activityMetadata.variableSets,
			activityMetadata.parameters,
			activityNode.data,
			variableScopeEnter,
			variableScopeLeave,
			`activity '${activityNode.data.Type}' (${activityNode.id})`,
			(expression, name, scope, variable) => this.getPropertyTypeFromExpression(expression, name, scope, variable || ({} as VariableTypeMetadata))
		);

		// Process expression parameters to include their side effects on the variable scope
		for (const parameter of activityMetadata.parameters) {
			if (parameter.type === ParameterType.Expression) {
				const expressionValue = activityNode.data[parameter.name] as ExpressionModel | undefined;
				if (expressionValue) {
					await this.processExpressionSideEffects(expressionValue, variableScopeLeave);
				}
			}
		}

		return {
			enter: variableScopeEnter,
			leave: variableScopeLeave,
		};
	}

	/**
	 * Computes the variable scope that child activities inside a block activity inherit.
	 * It inherits from the parent's enter scope and adds any variables defined by the
	 * block's Activity-collection parameters' expectedType.variableSets (e.g. loop item variable).
	 */
	private async getParentContentScope(parentId: string): Promise<VariableScope> {
		const cached = this.parentContentScopeByActivity.get(parentId);
		if (cached) return cached;

		const parentNode = this.nodeById.get(parentId) as ActivityNode | undefined;
		if (!parentNode) {
			return this.rootVariableScope;
		}

		// Get the parent block's calculated scope
		const parentActivityScope = await this.calculateCachedScope(parentId);

		// Create the scope that children inside this block will see
		const contentScope = new VariableScope();
		contentScope.addPrevious(parentActivityScope.enter);

		// Process expectedType.variableSets from Activity-collection parameters (e.g. "Content")
		const parentMetadata = this.activityByName.get(parentNode.data.Type);
		if (parentMetadata?.isBlock) {
			for (const param of parentMetadata.parameters) {
				if (param.type === ParameterType.Activity && param.expectedType?.variableSets?.length) {
					await this.processVariableSets(
						param.expectedType.variableSets,
						parentMetadata.parameters,
						parentNode.data,
						parentActivityScope.enter,
						contentScope,
						`activity '${parentNode.data.Type}' (${parentId}) ${param.name}`,
						(expression, name, scope, variable) => this.getPropertyTypeFromExpression(expression, name, scope, variable ?? ({} as VariableTypeMetadata))
					);
				}
			}
		}

		this.parentContentScopeByActivity.set(parentId, contentScope);
		return contentScope;
	}

	/**
	 * Process the side effects of an expression on the variable scope
	 * by analyzing its metadata and applying any variableSets it defines
	 */
	private async processExpressionSideEffects(expression: ExpressionModel, variableScope: VariableScope): Promise<void> {
		const expressionMetadata = this.expressionByName.get(expression.Type);
		if (!expressionMetadata || !expressionMetadata.variableSets || expressionMetadata.variableSets.length === 0) {
			return;
		}

		// Process expression's variable sets
		await this.processVariableSets(
			expressionMetadata.variableSets,
			expressionMetadata.parameters,
			expression,
			variableScope,
			variableScope,
			`expression '${expression.Type}'`,
			(expression, name, scope, variable) => this.getPropertyTypeFromExpression(expression, name, scope, variable || ({} as VariableTypeMetadata))
		);

		// Recursively process nested expressions
		for (const parameter of expressionMetadata.parameters) {
			if (parameter.type === ParameterType.Expression) {
				const nestedExpression = expression[parameter.name] as ExpressionModel | undefined;
				if (nestedExpression) {
					await this.processExpressionSideEffects(nestedExpression, variableScope);
				}
			}
		}
	}

	private getPropertyTypeFromExpression(
		expression: ExpressionModel,
		name: string,
		variableScope: IVariableScope,
		variable: VariableTypeMetadata
	): ObjectPropertyType | undefined {
		if (expression) {
			if (expression.Type === ExpressionTypes.Const) {
				const constExp = expression as ConstExpressionModel;
				return this.resolveConstExpressionType(constExp, name);
			}
			if (expression.Type === ExpressionTypes.Var) {
				const varExp = expression as VarExpressionModel;
				if (varExp.Target) {
					const targetType = this.getPropertyTypeFromExpression(varExp.Target, name, variableScope, variable);
					const targetScope = this.getVariableScopeFromPropertyType(targetType);
					if (!targetScope) {
						return undefined;
					}
					return this.resolveVarExpressionType(varExp, targetScope, name);
				}
				// Note: Don't apply collection wrapping here - processVariableSets will do it
				return this.resolveVarExpressionType(varExp, variableScope, name);
			}

			// For other expression types, analyze using metadata
			const expressionMetadata = this.expressionByName.get(expression.Type);
			if (expressionMetadata) {
				return this.analyzeExpressionResultType(
					expression,
					expressionMetadata,
					variableScope,
					name,
					variable,
					(expr, n, scope, v) => this.getPropertyTypeFromExpression(expr, n, scope, v || variable),
					(n, type, _) => this.getVariableTypeAsPrimitive(n, type)
				);
			}
		}

		return undefined;
	}
}
